/* KIYO: actions for "Deine Konfiguration" groups in cart + drawer.
 *
 * Markup contract (snippets/cart-products.liquid):
 *   header row: .kiyo-config-group__header
 *     - button[data-kiyo-remove="<id>"]                  remove whole group
 *     - button[data-kiyo-share] + data-copied-label      share / copy link
 *     - button[data-kiyo-save]  + data-form-id, data-*     save via Klaviyo form
 *     - [data-kiyo-units="<id>"] with units buttons      quantity stepper
 *   carrier rows: tr[data-kiyo-config-line="<id>"] — hidden, carry the
 *     theme's data-key (line item key) and data-kiyo-base (qty at units=1)
 *
 * The parts list is a native <details>; no JS involved. All controls are
 * hidden until this module reveals them (progressive enhancement: without
 * JS the head shows plain text and no dead buttons).
 */
(() => {
  const reveal = () => {
    document
      .querySelectorAll('[data-kiyo-remove], [data-kiyo-share], [data-kiyo-save], .kiyo-config-group__units-btn')
      .forEach((el) => { el.hidden = false; });
  };

  const groupRows = (id) =>
    [...document.querySelectorAll(`[data-kiyo-config-line="${CSS.escape(id)}"]`)];

  const postUpdates = async (updates) => {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
  };

  const markBusy = (id) => {
    document.querySelectorAll(`[data-kiyo-config-id="${CSS.escape(id)}"]`)
      .forEach((row) => row.classList.add('kiyo-config-group--removing'));
  };

  // Remove ALL lines of a configuration in one go, then reload — the
  // server-rendered cart keeps page, drawer and bubble in sync.
  const removeGroup = async (id) => {
    const updates = {};
    groupRows(id).forEach((row) => {
      const key = row.getAttribute('data-key');
      if (key) updates[key] = 0;
    });
    if (!Object.keys(updates).length) return;
    markBusy(id);
    try {
      await postUpdates(updates);
    } catch (error) {
      console.warn('[Kiyo] Konfiguration entfernen fehlgeschlagen:', error);
    } finally {
      window.location.reload();
    }
  };

  // Scale ALL lines of a configuration to base x units.
  const setUnits = async (id, units) => {
    const updates = {};
    groupRows(id).forEach((row) => {
      const key = row.getAttribute('data-key');
      const base = parseInt(row.getAttribute('data-kiyo-base'), 10) || 1;
      if (key) updates[key] = base * units;
    });
    if (!Object.keys(updates).length) return;
    markBusy(id);
    try {
      await postUpdates(updates);
    } catch (error) {
      console.warn('[Kiyo] Anzahl aendern fehlgeschlagen:', error);
    } finally {
      window.location.reload();
    }
  };

  const shareConfig = async (button) => {
    const url = button.getAttribute('data-kiyo-share');
    if (!url) return;
    // Title makes the OS share sheet show "Meine Kiyo Konfiguration" instead
    // of the raw ?setup= URL (localized via data attribute from the snippet).
    const title = button.getAttribute('data-kiyo-share-title') || '';
    try {
      if (navigator.share) {
        await navigator.share(title ? { title, text: title, url } : { url });
        return;
      }
      throw new Error('no web share');
    } catch (error) {
      if (error && error.name === 'AbortError') return; // user closed the sheet
      try {
        await navigator.clipboard.writeText(url);
        const label = button.querySelector('span:last-child');
        const copied = button.getAttribute('data-copied-label');
        if (label && copied) {
          const original = label.textContent;
          label.textContent = copied;
          setTimeout(() => { label.textContent = original; }, 2000);
        }
      } catch (clipboardError) {
        console.warn('[Kiyo] Link teilen fehlgeschlagen:', clipboardError);
      }
    }
  };

  // ── Save: same Klaviyo path as the configurator ───────────────────────────
  // The configurator's "Speichern" opens a Klaviyo onsite form, then fires the
  // event "Configurator Saved". Doing exactly that here means the three live
  // Configurator flows (DE/EN/FR) work unchanged from the cart. Everything the
  // mail template renders (imageUrl, configUrl, addToCartUrl, breakdownText,
  // dimensionsText, shippingDuration, totalPrice) already sits on the line as
  // data attributes — verified against template RX9G8a on 2026-08-25.
  //
  // $event_id carries a ":cart" suffix on purpose: Klaviyo dedupes by that id,
  // so reusing the bare config id would silently swallow a cart save from
  // someone who had already saved the same build in the configurator — the
  // customer would click "Speichern" and never get a mail.

  const EMAIL_RE = /.+@.+\..+/;

  // Klaviyo's metaData keys depend on the form's field config, so try the known
  // variants and fall back to a key-name scan (same approach as the configurator).
  const extractEmail = (md) => {
    const m = (md && typeof md === 'object') ? md : {};
    const nested = [m.$consent, m.identifiers, m.profile].map((v) => (v && typeof v === 'object' ? v : {}));
    const candidates = [m.email, m.$email, m.EMAIL, m.Email].concat(nested.map((n) => n.email));
    for (const c of candidates) {
      if (typeof c === 'string' && EMAIL_RE.test(c.trim())) return c.trim();
    }
    for (const k of Object.keys(m)) {
      const v = m[k];
      if (k.toLowerCase().includes('email') && typeof v === 'string' && EMAIL_RE.test(v.trim())) return v.trim();
    }
    return '';
  };

  const extractFirstName = (md) => {
    const m = (md && typeof md === 'object') ? md : {};
    const v = m.$first_name ?? m.first_name ?? m.FirstName ?? m.firstName;
    return typeof v === 'string' ? v.trim() : '';
  };

  let pendingSave = null;

  const feedback = (button, label) => {
    const span = button.querySelector('span:last-child');
    if (!span || !label) return;
    const original = span.textContent;
    span.textContent = label;
    setTimeout(() => { span.textContent = original; }, 2500);
  };

  const trackSaved = (button, email, firstName) => {
    const d = button.dataset;
    const total = parseFloat(d.total);
    const props = {
      $event_id: `${d.kiyoSave}:cart`,
      source: 'cart',
      region: d.region || '',
      formEmail: email,
      breakdownText: d.parts || '',
      dimensionsText: d.dimensions || '',
      shippingDuration: d.shipping || '',
      currency: d.currency || '',
    };
    // Only send what is actually there — the mail template has its own
    // fallbacks and an empty string would suppress them.
    if (d.configUrl) props.configUrl = d.configUrl;
    if (d.imageUrl) props.imageUrl = d.imageUrl;
    if (d.cartUrl) props.addToCartUrl = d.cartUrl;
    if (Number.isFinite(total) && total > 0) props.totalPrice = total;

    const push = () => {
      const kl = window.klaviyo;
      if (!kl || typeof kl.push !== 'function') return false;
      try {
        if (email) kl.push(['identify', { $email: email.toLowerCase(), $first_name: firstName }]);
        kl.push(['track', 'Configurator Saved', props]);
        console.info('[Kiyo] Configurator Saved (Warenkorb) gesendet:', props.$event_id);
      } catch (error) {
        console.warn('[Kiyo] Klaviyo-Event fehlgeschlagen:', error);
      }
      return true; // an attempt happened — never retry on throw
    };
    // The submit event only fires after klaviyo.js loaded, so this is
    // practically always ready; one cheap retry as insurance.
    if (!push()) setTimeout(push, 800);
  };

  // Device-independent cart link for the mail's "Direkt bestellen" CTA. Liquid
  // only yields a token with key= sometimes; fetch it while the customer is
  // still typing their email, so the click path stays free of this request.
  const ensureCartUrl = (button) => {
    if (button.dataset.cartUrl) return;
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => {
        const token = (c && c.token) || '';
        if (token.indexOf('key=') > -1) button.dataset.cartUrl = `https://kiyo.eu/cart/c/${token}`;
      })
      .catch(() => { /* CTA falls back to the template default */ });
  };

  const saveConfig = (button) => {
    const formId = button.dataset.formId;
    if (!formId || pendingSave) return;
    pendingSave = { button, formId };
    button.setAttribute('aria-busy', 'true');
    ensureCartUrl(button);
    try {
      // Prefill for logged-in customers: an identified profile makes Klaviyo
      // fill the email field itself.
      const known = button.dataset.customerEmail;
      if (known && window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['identify', { $email: known.toLowerCase() }]);
      }
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    } catch (error) {
      console.warn('[Kiyo] Klaviyo-Formular liess sich nicht oeffnen:', error);
      button.removeAttribute('aria-busy');
      pendingSave = null;
    }
  };

  window.addEventListener('klaviyoForms', (event) => {
    const d = event.detail || {};
    if (!pendingSave || d.formId !== pendingSave.formId) return;
    const { button } = pendingSave;
    if (d.type === 'close') {
      button.removeAttribute('aria-busy');
      pendingSave = null;
      return;
    }
    if (d.type !== 'submit') return;
    button.removeAttribute('aria-busy');
    pendingSave = null;
    trackSaved(button, extractEmail(d.metaData), extractFirstName(d.metaData));
    feedback(button, button.dataset.savedLabel);
  });

  document.addEventListener('click', (event) => {
    const saveButton = event.target.closest('[data-kiyo-save]');
    if (saveButton) {
      saveConfig(saveButton);
      return;
    }
    const removeButton = event.target.closest('[data-kiyo-remove]');
    if (removeButton) {
      removeButton.disabled = true;
      removeGroup(removeButton.getAttribute('data-kiyo-remove'));
      return;
    }
    const shareButton = event.target.closest('[data-kiyo-share]');
    if (shareButton) {
      shareConfig(shareButton);
      return;
    }
    const unitsButton = event.target.closest('.kiyo-config-group__units-btn');
    if (unitsButton) {
      const wrap = unitsButton.closest('[data-kiyo-units]');
      const id = wrap.getAttribute('data-kiyo-units');
      const current = parseInt(wrap.querySelector('.kiyo-config-group__units-value')?.textContent, 10) || 1;
      const delta = parseInt(unitsButton.getAttribute('data-kiyo-units-delta'), 10) || 0;
      const next = Math.max(1, current + delta);
      if (next === current) return;
      wrap.querySelectorAll('.kiyo-config-group__units-btn').forEach((b) => { b.disabled = true; });
      setUnits(id, next);
    }
  });

  // Shopify's cart components morph rows back in after quantity updates on
  // other items — reveal our controls again whenever that happens.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      reveal();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  reveal();
})();
